import { computed } from 'vue';
import { useDialogStore } from '@/shared/stores/dialog';
import type { DialogOptions } from '@/shared/stores/dialog';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

/**
 * Dialog 알림창을 쉽게 사용할 수 있는 Composable
 *
 * @example
 * // 기본 사용 (Promise 기반)
 * const { confirm, alert } = useDialog();
 *
 * // 확인 다이얼로그
 * const result = await confirm({
 *   title: '삭제 확인',
 *   message: '정말 삭제하시겠습니까?'
 * });
 * if (result) {
 *   // 삭제 처리
 *
 * }
 *
 * // 알림 다이얼로그
 * await alert({
 *   title: '알림',
 *   message: '처리가 완료되었습니다.'
 * });
 *
 * // 간편 메서드 (title이 먼저, message가 나중)
 * await success('성공', '저장되었습니다');
 * await error('오류', '오류가 발생했습니다');
 * await warning('경고', '주의가 필요합니다');
 * await info('알림', '알림 정보입니다');
 */
export function useDialog() {
  const dialogStore = useDialogStore();
  const languageStore = useLanguageStore();
  const { createSyncT } = useTranslation();

  // 반응형 번역 함수
  const t = computed(() => {
    return createSyncT(languageStore.currentLanguage);
  });

  /**
   * 확인 다이얼로그 (확인/취소 버튼)
   */
  const confirm = async (options: DialogOptions | string): Promise<boolean> => {
    if (typeof options === 'string') {
      return dialogStore.showDialog({
        title: t.value('common.dialog.confirm') || 'Confirm',
        message: options,
        type: 'question',
        confirmText: t.value('common.confirm') || 'Confirm',
        cancelText: t.value('common.cancel') || 'Cancel',
      });
    }
    return dialogStore.showDialog({
      ...options,
      type: options.type || 'question',
      confirmText: options.confirmText || t.value('common.confirm') || 'Confirm',
      cancelText: options.cancelText !== undefined ? options.cancelText : (t.value('common.cancel') || 'Cancel'),
    });
  };

  /**
   * 알림 다이얼로그 (확인 버튼만)
   */
  const alert = async (options: DialogOptions | string): Promise<void> => {
    if (typeof options === 'string') {
      const dialogOptions: DialogOptions = {
        title: t.value('common.dialog.notification') || 'Notification',
        message: options,
        type: 'info',
        confirmText: t.value('common.ok') || 'OK',
        cancelText: '', // 취소 버튼 없음
      };

      console.warn('[useDialog.alert] enqueue', {
        show: dialogStore.show,
        title: dialogOptions.title,
        message: dialogOptions.message,
        type: dialogOptions.type,
      });

      const p = dialogStore.showDialog(dialogOptions);
      console.warn('[useDialog.alert] after enqueue', { show: dialogStore.show });
      await p;
      return;
    }

    const dialogOptions: DialogOptions = {
      ...options,
      type: options.type || 'info',
      confirmText: options.confirmText || t.value('common.ok') || 'OK',
      cancelText: options.cancelText !== undefined ? options.cancelText : '', // 알림은 기본적으로 취소 버튼 없음
    };

    console.warn('[useDialog.alert] enqueue', {
      show: dialogStore.show,
      title: dialogOptions.title,
      message: dialogOptions.message,
      type: dialogOptions.type,
    });

    const p = dialogStore.showDialog(dialogOptions);
    console.warn('[useDialog.alert] after enqueue', { show: dialogStore.show });
    await p;
  };

  /**
   * 간편 메서드들
   */
  const success = async (title: string, message: string, timeout?: number) => {
    await alert({ title, message, type: 'success', timeout, confirmText: t.value('common.ok') || 'OK' });
  };

  const error = async (title: string, message: string, timeout?: number) => {
    await alert({ title, message, type: 'error', timeout, confirmText: t.value('common.ok') || 'OK' });
  };

  const warning = async (title: string, message: string, timeout?: number) => {
    await alert({ title, message, type: 'warning', timeout, confirmText: t.value('common.ok') || 'OK' });
  };

  const info = async (title: string, message: string, timeout?: number) => {
    await alert({ title, message, type: 'info', timeout, confirmText: t.value('common.ok') || 'OK' });
  };

  /**
   * 삭제 확인 다이얼로그
   */
  const deleteConfirm = async (message?: string) => {
    return confirm({
      title: t.value('common.dialog.deleteConfirm') || 'Delete Confirmation',
      message: message || t.value('common.dialog.deleteMessage') || 'Are you sure you want to delete this?',
      type: 'warning',
      confirmText: t.value('common.delete') || 'Delete',
      cancelText: t.value('common.cancel') || 'Cancel',
    });
  };

  return {
    confirm,
    alert,
    success,
    error,
    warning,
    info,
    deleteConfirm,
  };
}

